import React from 'react';
import { Checkbox } from 'V2/Shared/Checkbox';
import { ErrorRetry } from 'V2/Shared/ErrorRetry';
import { Skeleton } from 'V2/Shared/Skeleton';
import styles from '../styles.module.css';

interface AgreementSectionProps {
  isLoading: boolean;
  isDisabled?: boolean;
  documents: Array<{ identifier: string; name: string; viewUrl: string }>;
  isSigned: boolean;
  isSigning: boolean;
  signError: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const AgreementSection: React.FC<AgreementSectionProps> = ({
  isLoading,
  isDisabled = false,
  documents,
  isSigned,
  isSigning,
  signError,
  onChange,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Defensive guard: Playwright dispatches clicks via CDP directly on the
    // visual icon <div> inside the label, bypassing the native disabled
    // semantics on the <input>. This ensures sign() is never called while
    // docs are still loading or the payment session isn't ready.
    if (isLoading || isDisabled) return;
    onChange(e);
  };
  const label =
    documents.length > 0 ? (
      <span>
        I confirm & agree with the{' '}
        {documents.map((doc, i) => (
          <React.Fragment key={doc.identifier}>
            <a
              href={doc.viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.docLink}
              onClick={(e) => e.stopPropagation()}
            >
              {doc.name}
            </a>
            {i < documents.length - 1 ? ', ' : ''}
          </React.Fragment>
        ))}
        , the{' '}
        <a
          href="https://clearcover.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.docLink}
          onClick={(e) => e.stopPropagation()}
        >
          Terms of Service
        </a>
        , and to all of the above statements.
      </span>
    ) : (
      'I confirm & agree with the Terms of Service and to all of the above statements.'
    );

  const resolvedLabel = isLoading ? (
    <div className={styles.agreementSkeletonLines}>
      <Skeleton />
      <Skeleton />
      <Skeleton width="75%" />
    </div>
  ) : (
    label
  );

  return (
    <div className={styles.agreementSection}>
      <Checkbox
        name="policy_agreement"
        label={resolvedLabel}
        checked={isSigned}
        disabled={isLoading || isDisabled}
        isLoading={isSigning}
        onChange={handleChange}
      />
      {signError && <ErrorRetry>{signError}</ErrorRetry>}
    </div>
  );
};
